<template>
  <div class="segmented" role="group" :aria-label="label || undefined">
    <button
      v-for="option in options" :key="option.id"
      type="button"
      class="segmented__btn"
      :class="{ 'segmented__btn--on': option.id === modelValue }"
      :aria-pressed="option.id === modelValue"
      :title="option.title || undefined"
      @click="$emit('update:modelValue', option.id)"
    >
      <span v-if="option.icon" class="segmented__icon" aria-hidden="true">{{ option.icon }}</span>
      <span v-if="option.label" class="segmented__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script setup>
/**
 * One control, several ways of looking at the same thing.
 *
 * Not tabs: `UiTabs` sits over the content and says which SECTION you are
 * reading; this one sits in a header beside the buttons and says which SHAPE
 * the same list is drawn in — a grid, a map, grouped by round. The difference
 * matters because a page shows one of each at most, and drawn alike they would
 * both claim to be the page's navigation.
 *
 * It exists because two screens were about to hold the same markup: the planner
 * of `work-orders` grew it first, and the list of `properties` was writing it
 * again. A component copied between modules is a bug (see this package's own
 * header), and the copy is also where the two drift: one gets the keyboard, the
 * other gets the mobile width, neither gets both.
 *
 * A segment is a STATE and not an action, which is why it is drawn as one box
 * with the current option filled rather than as three buttons: three buttons of
 * three weights next to each other read as three unrelated things, and the one
 * with a border reads as the one that is selected even when it is a link to
 * somewhere else. Where the option leads is the caller's business: this emits
 * the id and has no opinion on whether that flips a `ref` or pushes a route.
 */
defineProps({
  // [{ id, label?, icon?, title? }]. `icon` alone is legitimate for a control
  // that has to fit in a row of a card — give it a `title` when it does, or the
  // only people who can read it are the ones who already knew.
  options: { type: Array, required: true },
  modelValue: { type: String, default: '' },
  // What the group is, for whoever is listening rather than looking.
  label: { type: String, default: '' },
})

defineEmits(['update:modelValue'])
</script>

<style scoped>
/* The same height and the same type size as `UiButton` at `sm`, because this
   control's whole job is to stand next to those buttons in a page header: two
   pixels of difference there reads as a mistake even to somebody who cannot say
   what is wrong. The touch rule is copied for the same reason — if the buttons
   beside it grow for a finger and this does not, the row breaks on a phone. */
.segmented {
  display: inline-flex;
  height: 30px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.segmented__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 100%;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: none;
  border-right: 1px solid var(--color-border);
  font: inherit;
  font-size: var(--text-xs);
  font-weight: 550;
  color: var(--color-text-mute);
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--fast), color var(--fast);
}
.segmented__btn:last-child { border-right: none; }
.segmented__btn:hover { color: var(--color-text); }
.segmented__btn--on {
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: 600;
}
.segmented__btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

@media (pointer: coarse) {
  .segmented { height: 36px; }
}

/* 44 y no 36: es un control de VARIOS botones pegados, así que fallar el toque
   no es no llegar — es elegir el de al lado. */
@media (pointer: coarse) {
  .segmented { height: 44px; }
}
</style>
